import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
type Testimonial = {
  quote: string;
  name: string;
  role: string;
  avatar: string;
};
const testimonials: Testimonial[] = [{
  quote: 'Sentía que sabía astrología, pero cuando abría una carta veía demasiadas cosas al mismo tiempo. COSMOS me dio un orden claro y ahora sé exactamente por dónde empezar.',
  name: 'María G.',
  role: 'Astróloga Profesional',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Antes tardaba muchísimo haciendo una carta porque revisaba todo una y otra vez. Ahora mis lecturas son mucho más rápidas y seguras.',
  name: 'Valentina R.',
  role: 'Consultora Astrológica',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Lo que más me sorprendió fue que no aprendí más astrología; aprendí a organizar la que ya sabía.',
  name: 'Lucía M.',
  role: 'Estudiante de Astrología',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Mis interpretaciones dejaron de sentirse como definiciones separadas y empezaron a sentirse como historias reales.',
  name: 'Camila S.',
  role: 'Astróloga',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Siempre pensaba que me faltaba estudiar más. Me di cuenta de que lo que me faltaba era un método.',
  name: 'Sofía A.',
  role: 'Consultora Holística',
  avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Ahora puedo abrir una carta y detectar rápidamente qué es lo más importante sin sentirme bloqueada.',
  name: 'Julieta F.',
  role: 'Estudiante Avanzada',
  avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Mis sesiones empezaron a sentirse mucho más profesionales porque ya no improviso mientras interpreto.',
  name: 'Victoria L.',
  role: 'Astróloga Profesional',
  avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'COSMOS me ayudó a pasar de sentir caos mental a sentir claridad cada vez que trabajo con una carta natal.',
  name: 'Carolina M.',
  role: 'Terapeuta Astrológica',
  avatar: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?w=300&h=300&fit=crop&crop=face'
}];
// Pre-computed star particle positions to avoid hydration mismatches
const stars = Array.from({
  length: 40
}, (_, i) => ({
  id: i,
  top: `${i * 37 % 100}%`,
  left: `${i * 53 % 100}%`,
  size: i % 3 + 1,
  delay: i % 5 * 0.7,
  duration: 3 + i % 4
}));
function TestimonialCard({
  t
}: {
  t: Testimonial;
}) {
  return <motion.div whileHover={{
    scale: 1.04,
    y: -6
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 22
  }} className="relative w-[340px] md:w-[380px] shrink-0 mx-4 select-none" data-id="element-156">
      {/* Glassmorphism card */}
      <div className="relative h-full rounded-2xl p-7 bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden" data-id="element-157">
        {/* Gold corner glow */}
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/20 rounded-full blur-3xl pointer-events-none" data-id="element-158"></div>
        <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-159"></div>

        {/* Decorative quote icon */}
        <Quote className="absolute top-4 right-4 w-8 h-8 text-primary/20" aria-hidden="true" data-id="element-160" />

        <div className="relative z-10" data-id="element-161">
          {/* Stars */}
          <div className="flex gap-1 mb-5" data-id="element-162">
            {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-primary text-primary drop-shadow-[0_0_4px_rgba(212,168,87,0.6)]" data-id="element-163" />)}
          </div>

          {/* Quote */}
          <p className="text-foreground/90 text-base leading-relaxed mb-6 italic min-h-[140px]" data-id="element-164">
            "{t.quote}"
          </p>

          {/* Author */}
          <div className="flex items-center gap-3 pt-4 border-t border-white/10" data-id="element-165">
            <div className="relative" data-id="element-166">
              <div className="absolute inset-0 bg-primary/30 rounded-full blur-md" data-id="element-167"></div>
              <img src={t.avatar} alt={t.name} className="relative w-12 h-12 rounded-full object-cover border-2 border-primary/40 shadow-lg" loading="lazy" data-id="element-168" />
            </div>
            <div data-id="element-169">
              <div className="font-semibold text-foreground" data-id="element-170">{t.name}</div>
              <div className="text-xs text-primary/80 uppercase tracking-wider font-medium" data-id="element-171">
                {t.role}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function TestimonialsMarquee() {
  // Duplicate for seamless loop
  const loop = [...testimonials, ...testimonials];
  return <section id="testimonios" className="relative py-28 overflow-hidden border-y border-white/5" style={{
    background: 'radial-gradient(ellipse at top, #0d1238 0%, #050518 60%, #020210 100%)'
  }} data-id="element-172">
      {/* Animated star particles */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-173">
        {stars.map(s => <motion.div key={s.id} className="absolute rounded-full bg-white" style={{
        top: s.top,
        left: s.left,
        width: s.size,
        height: s.size
      }} animate={{
        opacity: [0.2, 1, 0.2],
        scale: [0.8, 1.4, 0.8]
      }} transition={{
        duration: s.duration,
        delay: s.delay,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-174" />)}
      </div>

      {/* Decorative cosmic glows */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-175"></div>
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-176"></div>

      <div className="relative z-10 container mx-auto px-4 mb-16 text-center max-w-3xl" data-id="element-177">
        <h2 className="text-3xl md:text-5xl font-heading font-bold mb-5 text-foreground leading-tight" data-id="element-178">
          Lo que dicen quienes ya{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-179">
            organizaron su forma de interpretar
          </span>
        </h2>
        <p className="text-lg text-muted-foreground" data-id="element-180">
          Más claridad. Más seguridad. Menos caos.
        </p>
      </div>

      {/* Marquee */}
      <div className="relative z-10 group" data-id="element-181">
        {/* Edge fade masks */}
        <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[#050518] to-transparent z-20 pointer-events-none" data-id="element-182"></div>
        <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[#020210] to-transparent z-20 pointer-events-none" data-id="element-183"></div>

        <motion.div className="flex py-4" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: 50,
        ease: 'linear',
        repeat: Infinity
      }} style={{
        width: 'max-content'
      }} data-id="element-184">
          {loop.map((t, i) => <TestimonialCard key={i} t={t} data-id="element-185" />)}
        </motion.div>
      </div>
    </section>;
}